<!--
    LP-5358 / LP-5360: one supplier-allocation table, shared by the admin and Enterprise
    transaction detail views.

    Every data cell carries a `data-label`. At mobile widths the CSS turns each row into a stacked
    card and renders that label beside the value via ::before, so the column meaning survives
    without a visible header row — the header is what collides first when a financial table is
    squeezed, and dropping it while keeping per-cell labels is what makes the mobile layout
    readable instead of merely narrow.
-->
<div class="cashback-allocation-block">
    <!--
        LP-5358/LP-5360 (Codex PR #113, P2): movement-NEUTRAL copy. The heading said "cashback
        earned" and the note said "before and after this transaction's credit", which asserts a
        credit happened — but this table deliberately shows FAILED and PENDING legs too, and such a
        leg may have credited nothing at all. The status pill does not undo an accounting claim
        made in the surrounding prose. Status and the snapshots now establish whether anything
        moved; the prose only says which ledger the numbers belong to.
    -->
    <div class="cashback-allocation-header">Cashback Allocations &mdash; per supplier</div>

    <p class="cashback-allocation-ledger-note">
        Each row is one supplier leg of this transaction&rsquo;s cashback, with the trader&rsquo;s
        own cashback balance <strong>with that supplier</strong> either side of it. See Status for
        whether a leg settled. These are per-supplier balances &mdash; not the trader&rsquo;s main
        CZAR wallet and not the supplier&rsquo;s funding balance.
    </p>

    <!--
        The scroll container is the tablet fallback. It scrolls the TABLE only, never the page, so
        a wide financial row can never produce page-level horizontal scrolling.
    -->
    <div class="cashback-allocation-scroll">
        <div
            class="cashback-allocation-table"
            [class.has-execution-column]="showExecutionColumn"
            role="table"
            aria-label="Cashback allocations per supplier">
            <div class="cashback-allocation-row cashback-allocation-title" role="row">
                <div role="columnheader">Supplier</div>
                <div class="is-numeric" role="columnheader">Amount</div>
                <div role="columnheader">Status</div>
                <div class="is-numeric" role="columnheader">
                    Cashback with supplier <span class="col-head-qualifier">before</span>
                </div>
                <div class="is-numeric" role="columnheader">
                    Cashback with supplier <span class="col-head-qualifier">after</span>
                </div>

                @if (showExecutionColumn) {
                    <div role="columnheader">Execution / Batch</div>
                }

                <div role="columnheader">Blockchain</div>
            </div>

            @for (row of rows; track $index) {
                <div class="cashback-allocation-row" role="row">
                    <div class="allocation-supplier" data-label="Supplier" role="cell">
                        {{ row.supplierName || unavailableDisplay }}
                    </div>

                    <div class="is-numeric" data-label="Amount" role="cell">
                        @if (hasBalance(row.amount)) {
                            {{ balanceValue(row.amount) | number: '1.2-2' }}
                        } @else {
                            <span class="allocation-unavailable">{{ unavailableDisplay }}</span>
                        }
                    </div>

                    <div data-label="Status" role="cell">
                        @if (row.status) {
                            <span class="allocation-status" [ngClass]="statusClass(row.status)">
                                {{ row.status }}
                            </span>
                        } @else {
                            <span class="allocation-unavailable">{{ unavailableDisplay }}</span>
                        }
                    </div>

                    <div class="is-numeric" data-label="Cashback with supplier — before" role="cell">
                        @if (hasBalance(row.balanceBefore)) {
                            {{ balanceValue(row.balanceBefore) | number: '1.2-2' }}
                        } @else {
                            <span class="allocation-unavailable">{{ unavailableDisplay }}</span>
                        }
                    </div>

                    <div class="is-numeric" data-label="Cashback with supplier — after" role="cell">
                        @if (hasBalance(row.balanceAfter)) {
                            {{ balanceValue(row.balanceAfter) | number: '1.2-2' }}
                        } @else {
                            <span class="allocation-unavailable">{{ unavailableDisplay }}</span>
                        }
                    </div>

                    @if (showExecutionColumn) {
                        <div data-label="Execution / Batch" role="cell">
                            @if (row.executionRef) {
                                <span class="allocation-execution-ref">{{ row.executionRef }}</span>
                            } @else {
                                <span class="allocation-unavailable">{{ unavailableDisplay }}</span>
                            }
                        </div>
                    }

                    <div data-label="Blockchain" role="cell">
                        @if (row.blockchainUrl) {
                            <a
                                class="allocation-blockchain-link"
                                [href]="row.blockchainUrl"
                                target="_blank"
                                rel="noopener">
                                View
                            </a>
                        } @else {
                            <span class="allocation-unavailable">{{ unavailableDisplay }}</span>
                        }
                    </div>
                </div>
            }
        </div>
    </div>
</div>
